import { ref, computed, type Ref } from "vue";
import { getPack, createAnalysis, saveTaskSelection, saveSessionUserInput } from "../api";
import type { PackWithExecuting, RunAgentTask, ScribeFile, AnalysisMetadata } from "../types";
import { useScribeStore } from "@/stores/scribe";
import { useToast } from "vue-toastification";
import api from "@/api/index";
import { RecordingStatus } from "@/types/artifacts/Recordings";
import { AnalysisStatus } from "@/types/scribe/Analysis";
import { useRunAgentPlatforms } from "./useRunAgentPlatforms";
import { useRunAgentUserInput } from "./useRunAgentUserInput";
import { useLuckyReportQuota } from "./useLuckyReportQuota";
import { createLatestRequestScope } from "@/composables/useLatestRequest";
import { type ExcludedDocument, formatExcludedDocumentSize } from "@/components/common/entity/sparkyReportTypes";
import { couldNot } from "@/helpers/userMessages";
import { logError } from "@/helpers/logError";
import { isPdfUpload } from "@/helpers/files";

export interface UseRunAgentOrchestratorOptions {
	type: "project" | "conversation";
	id: Ref<string> | string;
	selectedFileIds?: Ref<string[]>;
	// LSP-18409: true when the caller is an editor on a cross-org shared project. The report
	// dispatches under the OWNER tenant, so pack detail must be read from the owner's tenant
	// (via the server proxy) — the owner's tenant has no idea about the caller's own packs.
	isExternalEditor?: Ref<boolean> | boolean;
}

// LSP-16433: list omitted documents for a user-facing toast, e.g. "survey.pdf (62 MB), plans.pdf (51 MB)".
function listExcludedDocuments(documents: ExcludedDocument[]): string {
	return documents
		.map((document) => {
			const name = document.fileName?.trim() || "a document";
			const size = formatExcludedDocumentSize(document.sizeBytes);
			return size ? `${name} (${size})` : name;
		})
		.join(", ");
}

const SELECT_MEDIA_MESSAGE = "Select a media item first.";

// LSP-20502: a failed run must never show the customer raw error text. The server surfaces a small
// set of CURATED reasons a run was refused, and those are worth showing verbatim — but the same
// catch blocks also see unexpected throws (a network stack's "TypeError: Failed to fetch", a
// stringified 500), which are developer-facing noise. Match the curated set explicitly and fall
// back to a plain sentence for everything else. LSP-19467 cleaned up the toast call sites; this is
// the same policy applied to the values that FEED them (ScribeFab.vue toasts `error.value`
// directly, so an unfiltered message assigned here still reaches the customer).
const GENERIC_RUN_FAILURE_MESSAGE = "We were unable to run your report. Please try again later.";

// Exact-match curated reasons thrown by contact-api's sparks controller (src/controllers/sparks.ts).
const CURATED_RUN_FAILURE_MESSAGES = [
	// LUCKY_REPORT_LIMIT_MESSAGE (sparks.ts) — also produced client-side by the quota pre-check below.
	"You've used all of your free reports. Contact sales@liveswitch.com to upgrade.",
	"View Only accounts cannot run reports."
];

// Client-side title validation, surfaced by contact-api as "Report title must be <n> characters or fewer."
const REPORT_TITLE_MESSAGE_PREFIX = "Report title must be ";

// `Project <id> has no media to analyze( (<names> exceeded the size limit and were omitted))?.`
// The tail names the omitted documents and is genuinely useful; the leading "Project <uuid>" is
// not, so the id is dropped and the rest kept.
const PROJECT_NO_MEDIA_PATTERN = /^Project\s+\S+\s+has no media to analyze(.*)\.$/;

// The server's oversized-media refusals NAME the offending file ("plans.pdf (80 MB) is too large to
// analyze. No analyzable media remains."), which is the whole actionable part of the message —
// replacing it with the generic sentence would tell the customer their run failed but not that they
// need to remove a specific document (LSP-16433 / LSP-18409 rely on this reaching the toast). These
// phrases are server-authored prose; no network or runtime error produces them.
const CURATED_RUN_FAILURE_PHRASES = ["is too large to analyze", "are too large to analyze", "No analyzable media remains"];

export function toUserFacingRunError(rawMessage: unknown): string {
	if (typeof rawMessage !== "string") return GENERIC_RUN_FAILURE_MESSAGE;
	const trimmed = rawMessage.trim();
	if (!trimmed) return GENERIC_RUN_FAILURE_MESSAGE;
	if (CURATED_RUN_FAILURE_MESSAGES.includes(trimmed)) return trimmed;
	if (trimmed.startsWith(REPORT_TITLE_MESSAGE_PREFIX)) return trimmed;
	if (CURATED_RUN_FAILURE_PHRASES.some((phrase) => trimmed.includes(phrase))) return trimmed;
	const noMedia = PROJECT_NO_MEDIA_PATTERN.exec(trimmed);
	if (noMedia) return `There's nothing for Sparky to analyze in this project${noMedia[1]}.`;
	return GENERIC_RUN_FAILURE_MESSAGE;
}

// Reads the message off a thrown error the same way every run path does, then filters it.
export function toUserFacingRunErrorFromThrown(error: any): string {
	return toUserFacingRunError(error?.response?.data?.message ?? error?.data?.message ?? error?.message);
}

// Mirrors contact-api's MAX_REPORT_TITLE_LENGTH (apps/contact-api/src/helpers/scribeAnalysisTitle.ts).
// The project-run dispatch path (createProjectSpark) already enforces this server-side via
// takeGraphemes, but the conversation-run path sends its title straight to Scribe from the browser
// (LSP-16433 Option D — contact-api only size-gates media, it does not dispatch) with no length
// check at all now that the title is user-editable, so this needs to be enforced here too.
const MAX_REPORT_TITLE_LENGTH = 255;
const titleGraphemeSegmenter = new Intl.Segmenter("en", { granularity: "grapheme" });

function clampTitleGraphemes(title: string): string {
	return Array.from(titleGraphemeSegmenter.segment(title), ({ segment }) => segment)
		.slice(0, MAX_REPORT_TITLE_LENGTH)
		.join("");
}

// Mirrors contact-api's validateScribeAnalysisTitle (apps/contact-api/src/helpers/scribeAnalysisTitle.ts):
// the entity name is the identifying part of an auto-generated title, so it must never be the part
// that gets cut — not even to make room for the " (Run N)" ordinal suffix computeReportNameDraft
// appends below (Bugbot finding: an earlier version reserved suffix room INSIDE this function's
// budget, which could truncate the entity name itself). Always uses the full 255-grapheme budget;
// the caller is responsible for dropping anything it appends afterward if there's no room left.
function composeAutoTitle(packName: string, entityName: string): string {
	const entityGraphemeCount = Array.from(titleGraphemeSegmenter.segment(entityName)).length;
	const availablePrefixLength = MAX_REPORT_TITLE_LENGTH - entityGraphemeCount - 3;
	if (availablePrefixLength <= 0) return clampTitleGraphemes(entityName);
	const prefix = Array.from(titleGraphemeSegmenter.segment(packName), ({ segment }) => segment)
		.slice(0, availablePrefixLength)
		.join("");
	return prefix ? `${prefix} - ${entityName}` : clampTitleGraphemes(entityName);
}

const mergeAnalysisMetadata = (clientMetadata: AnalysisMetadata, gateMetadata?: Partial<AnalysisMetadata> | null): AnalysisMetadata =>
	gateMetadata ? (gateMetadata as AnalysisMetadata) : clientMetadata;

export function useRunAgentOrchestrator(options: UseRunAgentOrchestratorOptions) {
	const toast = useToast();
	const store = useScribeStore();
	const luckyQuota = useLuckyReportQuota();

	// LSP-16996: guards against overlapping loadPackDetails() calls (e.g. the user re-selecting a
	// pack while a slower earlier load is still in flight). Only the latest load may apply its
	// results; a superseded load discards them so a stale slower response can never overwrite the
	// newer selected pack's assets/metadata/task selection. Uses the plain factory (not the
	// useLatestRequest hook) so the composable stays usable outside a component setup context.
	const loadRequests = createLatestRequestScope();

	const getId = () => (typeof options.id === "string" ? options.id : options.id.value);
	const isOwnerTenantProject = () =>
		options.type === "project" &&
		(typeof options.isExternalEditor === "boolean" ? options.isExternalEditor : options.isExternalEditor?.value === true);

	// State
	const selectedPack = ref<PackWithExecuting | null>(null);
	const loadedPackTasks = ref<RunAgentTask[]>([]);
	const pendingAssets: Ref<ScribeFile[] | null> = ref(null);
	const pendingMetadata: Ref<AnalysisMetadata | null> = ref(null);
	// Pre-filled, user-editable report name — shown in RunAgentDialog.vue when it opens (packs that
	// skip the dialog entirely just run with this value as-is, per the "no extra clicks" decision).
	// Always kept current so runAnalysis() has a real title to send even when the dialog never shows.
	const reportNameDraft = ref("");
	const runningAnalysis = ref(false);
	const loadingPack = ref(false);
	const error = ref<string | null>(null);
	const selectedTaskIds = ref<string[]>([]);

	// LSP-15921 follow-up: cross-tab guard. Each browser tab owns an
	// independent runningAnalysis ref, so the single-tab re-entry guard below
	// cannot stop two tabs from racing the same pack on the same entity.
	// The TTL-backed localStorage entry is the single source of truth — every
	// runAnalysis() call re-reads it. We deliberately do not register a
	// BroadcastChannel/storage listener: a stale `peerRunning` ref left over
	// from a crashed peer would block this tab until reload, whereas
	// re-reading localStorage on demand is self-healing once TTL elapses.
	const CROSS_TAB_LOCK_TTL_MS = 5 * 60 * 1000;
	const crossTabTabId =
		typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random().toString(36).slice(2)}`;

	function crossTabLockKey(): string | null {
		const pack = selectedPack.value;
		if (!pack) return null;
		return `sparky-run:${options.type}:${getId()}:${pack.id}`;
	}

	function crossTabReadLock(key: string): { ts: number; tabId: string } | null {
		try {
			const raw = localStorage.getItem(key);
			if (!raw) return null;
			const parsed = JSON.parse(raw) as unknown;
			// Reject malformed payloads (any tab/extension could write {} or a
			// string under this key). Without this guard, `parsed.tabId` would
			// be undefined and `undefined !== crossTabTabId` would falsely flag
			// the lock as peer-held, blocking this tab until TTL.
			if (
				typeof parsed !== "object" ||
				parsed === null ||
				typeof (parsed as { ts?: unknown }).ts !== "number" ||
				typeof (parsed as { tabId?: unknown }).tabId !== "string"
			) {
				console.warn("[runAnalysis] cross-tab lock payload malformed — clearing", { key, raw });
				localStorage.removeItem(key);
				return null;
			}
			const validLock = parsed as { ts: number; tabId: string };
			if (Date.now() - validLock.ts > CROSS_TAB_LOCK_TTL_MS) {
				localStorage.removeItem(key);
				return null;
			}
			return validLock;
		} catch (e) {
			console.warn("[runAnalysis] cross-tab lock parse failed", { key, error: String(e) });
			return null;
		}
	}

	function crossTabPeerLockHeld(): boolean {
		const key = crossTabLockKey();
		if (!key) return false;
		const lock = crossTabReadLock(key);
		return lock !== null && lock.tabId !== crossTabTabId;
	}

	// Media count tracking for warnings
	const totalMediaCount = ref(0);
	const readyMediaCount = ref(0);
	const selectedMediaCount = ref(0);
	const usingSelectedMedia = computed(() => {
		const fileIds = options.selectedFileIds?.value;
		return !!(fileIds && fileIds.length > 0);
	});

	// Warning computations
	const hasNonReadyMedia = computed(() => totalMediaCount.value > readyMediaCount.value);
	const nonReadyMediaCount = computed(() => totalMediaCount.value - readyMediaCount.value);
	const mediaWarnings = computed(() => {
		const warnings: string[] = [];

		if (hasNonReadyMedia.value) {
			const count = nonReadyMediaCount.value;
			if (count === 1) {
				warnings.push(`I noticed 1 file is still processing, so I won't be able to include it in my analysis just yet.`);
			} else {
				warnings.push(`I noticed ${count} files are still processing, so I won't be able to include them in my analysis just yet.`);
			}
		}

		if (usingSelectedMedia.value) {
			const count = options.selectedFileIds?.value?.length || 0;
			if (count === 1) {
				warnings.push(`You've selected 1 specific file for me to analyze, so I won't look at your other content.`);
			} else if (count > 0) {
				warnings.push(`You've selected ${count} specific files for me to analyze, so I won't look at your other content.`);
			}
		}

		return warnings;
	});

	// Sub-composables
	const platformComposable = useRunAgentPlatforms({ loadedPackTasks });
	const {
		hasPlatformRequirements,
		allPlatformsAuthenticated,
		computePlatformRequirements,
		loadPlatformsIfNeeded,
		reset: resetPlatforms
	} = platformComposable;

	const userInputComposable = useRunAgentUserInput({ loadedPackTasks });
	const { hasUserInputItems, userInputData, fetchUserInputConfigsForPack, reset: resetUserInput } = userInputComposable;

	// Key computed: does the dialog need to be shown?
	const needsDialog = computed(() => {
		return (hasPlatformRequirements.value && !allPlatformsAuthenticated.value) || hasUserInputItems.value;
	});

	// Asset gathering
	const filterBySelection = <T extends { id: string; MasterRecording?: { id?: string } | null }>(items: T[]): T[] => {
		const fileIds = options.selectedFileIds?.value;
		if (!fileIds || fileIds.length === 0) return items;
		const selectedIds = new Set(fileIds);
		return items.filter((item) => selectedIds.has(item.id) || selectedIds.has(item.MasterRecording?.id ?? ""));
	};

	const squashRecordings = (recordings: any[]): ScribeFile[] => {
		const result = [];
		for (const recording of recordings) {
			if (
				!recording.MasterRecording ||
				!recording.MasterRecording.privateUrl ||
				recording.recordingStatus !== RecordingStatus.Ready
			) {
				continue;
			}
			const conversationId =
				recording.conversationId ||
				recording.MasterRecording.conversationId ||
				(options.type === "conversation" ? getId() : undefined);
			result.push({
				externalId: recording.MasterRecording.id,
				externalType: "masterRecording",
				externalSource: "contact",
				conversationId,
				path: recording.MasterRecording.privateUrl,
				createdAt: new Date(recording.MasterRecording.createdAt)
			});
		}
		return result.sort((a: any, b: any) => (a.createdAt > b.createdAt ? 1 : -1));
	};

	const squashSnapshots = (snapshots: any[]): ScribeFile[] => {
		const result = [];
		for (const snapshot of snapshots) {
			if (!snapshot.MasterDocument || !snapshot.MasterDocument.privateUrl) {
				continue;
			}
			result.push({
				externalId: snapshot.MasterDocument.id,
				externalType: "masterDocument",
				externalSource: "contact",
				path: snapshot.MasterDocument.privateUrl,
				createdAt: new Date(snapshot.MasterDocument.createdAt)
			});
		}
		return result.sort((a: any, b: any) => (a.createdAt > b.createdAt ? 1 : -1));
	};

	// The shared rule, so the media Scribe sends and the ready/selected counts agree.
	const isPdfFile = (file: any): boolean => isPdfUpload(file);

	const squashPdfFiles = (files: any[]): ScribeFile[] => {
		const result = [];
		for (const file of files) {
			const path = file?.uploadResult?.signedUrl || file?.MasterDocument?.privateUrl || file?.privateUrl;
			if (!file.id || !path || !isPdfFile(file)) {
				continue;
			}
			result.push({
				externalId: file.id,
				externalType: "file",
				externalSource: "contact",
				path,
				mimeType: "application/pdf",
				createdAt: new Date(file.createdAt || file.MasterDocument?.createdAt)
			});
		}
		return result.sort((a: any, b: any) => (a.createdAt > b.createdAt ? 1 : -1));
	};

	interface MediaCounts {
		total: number;
		ready: number;
		selected: number;
	}

	// Pure media processing: computes assets + counts WITHOUT mutating any ref. The count refs are
	// applied by loadPackDetails under its latest-request guard (LSP-16996) — computing counts as a
	// side-effect here would let a superseded slower load overwrite the newer load's counts, since
	// this runs inside the awaited getAssets() before that guard.
	const processMedia = (
		snapshotResponse: any[],
		recordingResponse: any[],
		fileResponse: any[] = []
	): { assets: ScribeFile[]; hasAnyMedia: boolean; counts: MediaCounts } => {
		const pdfFiles = fileResponse.filter(isPdfFile);
		// Count total media before any filtering
		const total = snapshotResponse.length + recordingResponse.length + pdfFiles.length;

		// Process media with ready filtering
		const readySnapshots = squashSnapshots(snapshotResponse);
		const readyRecordings = squashRecordings(recordingResponse);
		const readyPdfFiles = squashPdfFiles(pdfFiles);
		const ready = readySnapshots.length + readyRecordings.length + readyPdfFiles.length;

		// Selected media count based on actual user selection
		const fileIds = options.selectedFileIds?.value;
		const selected = fileIds && fileIds.length > 0 ? fileIds.length : ready;

		// Apply selection filtering to get final assets
		const selectedSnapshots = filterBySelection(snapshotResponse);
		const selectedRecordings = filterBySelection(recordingResponse);
		const selectedPdfFiles = filterBySelection(pdfFiles);
		const finalAssets = [
			...squashSnapshots(selectedSnapshots),
			...squashRecordings(selectedRecordings),
			...squashPdfFiles(selectedPdfFiles)
		];
		// `hasAnyMedia` is pre-ready-filter — distinguishes "still processing" (retry) from
		// "nothing in scope" (terminal) when `assets` ends up empty.
		return {
			assets: finalAssets,
			hasAnyMedia: selectedSnapshots.length > 0 || selectedRecordings.length > 0 || selectedPdfFiles.length > 0,
			counts: { total, ready, selected }
		};
	};

	// Opt-out: when "false", skip the documents fetch entirely (not just filter).
	const includeDocuments = window.env?.VITE_SPARKY_INCLUDE_DOCUMENTS !== "false";

	const getAssets = async (): Promise<{ assets: ScribeFile[]; hasAnyMedia: boolean; counts: MediaCounts }> => {
		const id = getId();
		const where = options.type === "conversation" ? { conversationId: id } : { projectId: id };

		const PAGE_SIZE = 1000;
		const MAX_PAGES = 50;

		// LSP-16996 / LSP-20270: both the generic list API (conversation scope) and the
		// project-scoped /projects/:id/files+snapshots routes cap a single page at 1000 rows
		// (server-side default, LSP-20270), so fetch every page until a short/empty page
		// arrives — otherwise a project or conversation with more than 1000 assets, or an
		// explicit user media selection reaching past the newest 1000, would be silently
		// dropped from the analyzed set.
		//
		// MEDIUM re-verification finding (2026-09-24): comparing a page's length against the
		// REQUESTED PAGE_SIZE (1000) breaks if the server's MAX_PAGE_SIZE is ever configured
		// below 1000 (a bounded load-test window, see resolveMaxPageSize.ts) — every response
		// would then look "short" and this loop would stop after page 1, silently dropping
		// everything past the server's real cap. Compare against the size of the actual FIRST
		// page received instead, since that's the server's real effective page size regardless
		// of what PAGE_SIZE was requested. The unavoidable cost: when the true total is an exact
		// multiple of that effective size (most commonly: fewer than 1000 assets and no server
		// cap in effect), one extra, empty confirmation page is fetched before stopping —
		// correctness over saving that one round trip.
		const fetchAllPages = async (fetchPage: (page: number) => Promise<any[]>): Promise<any[]> => {
			const all: any[] = [];
			const seenIds = new Set<string>();
			// LSP-16996: a stable total order already means the server returns each row on
			// exactly one page. Dedupe by row id anyway so a concurrent insert shifting the
			// offset window — or a server that ignores the tiebreakers — can never double-count
			// an asset in the analyzed set. A real offset-shift duplicate returns the same row
			// with identical data, so keeping the first occurrence is sufficient. Rows without a
			// string id pass through unconditionally.
			const addUnique = (batch: any[]): void => {
				for (const row of batch) {
					const rowId = row?.id;
					if (typeof rowId === "string") {
						if (seenIds.has(rowId)) {
							continue;
						}
						seenIds.add(rowId);
					}
					all.push(row);
				}
			};
			let effectivePageSize: number | undefined;
			for (let page = 1; page <= MAX_PAGES; page++) {
				const batch = await fetchPage(page);
				addUnique(batch);
				if (batch.length === 0) {
					return all;
				}
				if (effectivePageSize === undefined) {
					effectivePageSize = batch.length;
				} else if (batch.length < effectivePageSize) {
					return all;
				}
			}
			// Reached the cap without a short page — surface it rather than silently truncating.
			// Kept as `conversationId` for conversation scope so this pre-existing (LSP-16996)
			// log field's meaning doesn't change for any downstream log-based alerting.
			console.warn("[getAssets] media pagination hit MAX_PAGES cap; results may be incomplete", {
				...(options.type === "conversation" ? { conversationId: id } : { projectId: id }),
				maxPages: MAX_PAGES,
				fetched: all.length
			});
			return all;
		};

		if (options.type === "project") {
			// LSP-20270 review finding: getSnapshotsForProject/getFilesForProject now cap a
			// single response at 1000 rows server-side. Without paging here, a project with
			// more than 1000 snapshots/files silently omitted assets from the report, and an
			// explicit user media selection (filterBySelection) reaching past the newest 1000
			// was silently dropped even though the selection dialog still claimed it was honored.
			const snapshotsPromise = fetchAllPages((page) => api.getSnapshotsForProject(id, { page, pageSize: PAGE_SIZE }));
			const filesPromise = includeDocuments
				? fetchAllPages((page) => api.getFilesForProject(id, { page, pageSize: PAGE_SIZE }))
				: Promise.resolve([] as any[]);
			const [snapshotResponse, recordingResponse, fileResponse] = await Promise.all([
				snapshotsPromise,
				api.getRecordingsForProject(id),
				filesPromise
			]);
			return processMedia(snapshotResponse, recordingResponse, fileResponse);
		} else if (options.type === "conversation") {
			// Build a fresh order array per page call so the shared reference can never be mutated
			// across pages (index, then createdAt, then the unique id → a deterministic total order).
			const buildStableOrder = () => [
				["index", "DESC"],
				["createdAt", "DESC"],
				["id", "ASC"]
			];

			const snapshotsPromise = fetchAllPages((page) =>
				api.getSnapshots({ where, order: buildStableOrder(), page, pageSize: PAGE_SIZE })
			);
			const filesPromise = includeDocuments
				? fetchAllPages((page) => api.getFiles({ where, order: buildStableOrder(), page, pageSize: PAGE_SIZE }))
				: Promise.resolve([] as any[]);

			const [snapshotResponse, recordingResponse, fileResponse] = await Promise.all([
				snapshotsPromise,
				api.getRecordings(id),
				filesPromise
			]);
			return processMedia(snapshotResponse, recordingResponse, fileResponse);
		}

		// No type match — no media, zero counts.
		return { assets: [], hasAnyMedia: false, counts: { total: 0, ready: 0, selected: 0 } };
	};

	const getMetadata = async (): Promise<AnalysisMetadata> => {
		const id = getId();
		const me = await api.getMe();
		const meta: AnalysisMetadata = {
			me: {
				name: me.user.name,
				email: me.user.email,
				phone: me.user.phone,
				id: me.user.id,
				organizationName: me.organization.name
			},
			notes: "",
			customer: {
				name: "",
				id: "",
				firstname: "",
				lastname: "",
				companyName: null,
				organizationId: "",
				createdAt: "",
				updatedAt: "",
				createdBy: "",
				updatedBy: null,
				deletedBy: null,
				deletedAt: null,
				Participants: {
					id: "",
					userId: null,
					customerId: "",
					conversationId: "",
					websiteId: "",
					organizationId: "",
					notificationEmail: false,
					notificationSMS: false,
					email: null,
					phone: "",
					joinTime: null,
					createdAt: "",
					updatedAt: "",
					deletedAt: null
				},
				PhoneNumbers: [],
				EmailAddresses: []
			}
		};

		if (options.type === "conversation") {
			const response = await api.getConversation(id);
			meta.notes = response.notes || "";
			if (response.Customers?.[0]) {
				meta.customer = response.Customers[0] as any;
			}
			meta.conversation = {
				id: response.id,
				name: response.name,
				externalId: response.externalId
			};
		} else if (options.type === "project") {
			const response = await api.getProject(id);
			meta.notes = typeof (response as { notes?: string | null }).notes === "string" ? response.notes || "" : "";
			if (response.Customers?.[0]) {
				meta.customer = response.Customers[0] as any;
			}
			meta.project = {
				id: response.id,
				name: response.name,
				externalId: response.externalId
			};
		}
		return meta;
	};

	// Caches the last entity's existing-sparks list (used only to count prior runs of a pack for the
	// ordinal below) so switching between pack candidates in the same dialog session re-filters the
	// same response instead of re-fetching it. Invalidated whenever a run actually succeeds (see
	// runAnalysis()), since that changes the true count for a same-session reopen.
	let existingSparksCache: { entityId: string; data: Array<{ packId?: string }> } | null = null;

	// break-it round 2: `existing.data` is API-shaped, not schema-validated — a malformed-but-resolved
	// response (not an array, or containing a non-object element) must degrade to "no prior runs
	// found" like a thrown error does, not crash the whole pack-load with a raw TypeError.
	const isPackIdRow = (value: unknown): value is { packId?: string } => value !== null && typeof value === "object";

	const fetchExistingSparksForOrdinal = async (entityId: string, requestId: number): Promise<Array<{ packId?: string }>> => {
		if (existingSparksCache?.entityId === entityId) {
			return existingSparksCache.data;
		}
		try {
			const existing = options.type === "project" ? await api.getProjectSparks(entityId) : await api.getSparks(entityId);
			const data = Array.isArray(existing?.data) ? existing.data.filter(isPackIdRow) : [];
			// break-it round 2: a slower, now-superseded load's fetch for a DIFFERENT (older) entity
			// can resolve after a newer load already cached the current entity — writing here
			// unconditionally would poison the newer entity's cache entry with the stale one's data.
			if (!loadRequests.isSuperseded(requestId)) {
				existingSparksCache = { entityId, data };
			}
			return data;
		} catch (e) {
			console.warn("[useRunAgentOrchestrator] failed to fetch existing sparks for the run ordinal", e);
			return [];
		}
	};

	// The plain "<pack> - <entity>" title is identical across repeat runs of the same pack against
	// the same target, so count prior runs here and append "(Run N)" from the second run onward.
	// Reuses the report-list calls EntityReports.vue already makes rather than a new endpoint.
	//
	// Project-run counts aren't scoped to "project-level analyses only" — getProjectSparks returns
	// the project's own analyses AND its child conversations', with no client-visible field to
	// separate them, so this is a monotonically increasing, merely-approximate ordinal.
	const computeReportNameDraft = (
		pack: PackWithExecuting,
		meta: AnalysisMetadata,
		existingSparks: Array<{ packId?: string }>
	): string => {
		const entityName = options.type === "conversation" ? meta.conversation?.name : meta.project?.name;
		const priorRunCount = existingSparks.filter((item) => item.packId === pack.id).length;
		const suffix = priorRunCount > 0 ? ` (Run ${priorRunCount + 1})` : "";

		// This prefill is what actually reaches runAnalysis()/createProjectSpark in the common case —
		// loadPackDetails always sets it, so the field is never really "empty" (Bugbot finding: the
		// entity-priority composition below needs to happen HERE, not only in runAnalysis()'s
		// already-unreachable empty-draft fallback).
		if (pack.name && entityName) {
			const composed = composeAutoTitle(pack.name, entityName);
			if (!suffix) return composed;
			// The ordinal suffix is the least important part of the title — if appending it would
			// overflow 255 graphemes, drop the suffix rather than truncate the (already entity-safe)
			// composed title (Bugbot finding: an earlier version instead shrank composeAutoTitle's
			// budget for the suffix, which could truncate the entity name itself to make room for it).
			const combinedGraphemeCount = Array.from(titleGraphemeSegmenter.segment(composed + suffix)).length;
			return combinedGraphemeCount <= MAX_REPORT_TITLE_LENGTH ? composed + suffix : composed;
		}
		const baseTitle = pack.name || entityName || "";
		if (!baseTitle) return "";
		// Same drop-the-suffix-rather-than-truncate rule as the both-names path above (Bugbot finding:
		// this single-name fallback still clamped baseTitle + suffix together, which could cut the name
		// itself to make room for the ordinal — exactly what the ordinal change was meant to prevent).
		if (!suffix) return clampTitleGraphemes(baseTitle);
		const clampedBase = clampTitleGraphemes(baseTitle);
		const combinedGraphemeCount = Array.from(titleGraphemeSegmenter.segment(clampedBase + suffix)).length;
		return combinedGraphemeCount <= MAX_REPORT_TITLE_LENGTH ? clampedBase + suffix : clampedBase;
	};

	// Load pack details: gather assets, fetch pack, load platforms, compute requirements, fetch user input
	const loadPackDetails = async (pack: PackWithExecuting) => {
		// LSP-16996: claim the latest-load slot. Any earlier in-flight load becomes superseded and
		// must not apply its results below.
		const { requestId } = loadRequests.startRequest();
		selectedPack.value = pack;
		error.value = "";
		loadingPack.value = true;
		// LSP-16996: clear ALL pack-derived state left by a prior load before starting a new one, so a
		// failed or empty reload can never surface stale data from a previous successful load (the
		// paginated media fetch adds real mid-load failure surface). This covers the run assets/metadata
		// AND the dialog-driving state (tasks, task selection, user-input configs, platform
		// requirements) so `needsDialog` cannot stay true for a load that bailed.
		pendingAssets.value = null;
		pendingMetadata.value = null;
		reportNameDraft.value = "";
		totalMediaCount.value = 0;
		readyMediaCount.value = 0;
		selectedMediaCount.value = 0;
		loadedPackTasks.value = [];
		selectedTaskIds.value = [];
		resetUserInput();
		computePlatformRequirements([]);

		try {
			// Fetch all independent data in parallel — includes the existing-sparks list the run-ordinal
			// name draft below needs; it depends only on entityId (known now), not on meta/packDetail, so
			// starting it here avoids adding a full round-trip of latency after this batch resolves.
			const [{ assets, hasAnyMedia, counts }, meta, packDetail, existingSparks] = await Promise.all([
				getAssets(),
				getMetadata(),
				isOwnerTenantProject() ? api.getProjectPack(getId(), pack.id) : getPack(pack.id),
				fetchExistingSparksForOrdinal(getId(), requestId),
				loadPlatformsIfNeeded()
			]);

			// A newer loadPackDetails() started while we were awaiting — discard this stale load's
			// results so they cannot overwrite the newer selected pack's state.
			if (loadRequests.isSuperseded(requestId)) {
				return;
			}

			// Apply media counts under the guard (getAssets computes them but does not mutate refs),
			// so a superseded slower load cannot overwrite the newer load's counts.
			totalMediaCount.value = counts.total;
			readyMediaCount.value = counts.ready;
			selectedMediaCount.value = counts.selected;

			// LSP-15545: `assets` merges ready photos (`masterDocument`), videos
			// (`masterRecording`), and PDF files; empty == no analyzable media. `hasAnyMedia`
			// flips between retryable (something processing) and terminal (session has nothing);
			// the terminal copy also drops PDF wording when `includeDocuments` is false.
			if (assets.length === 0) {
				if (hasAnyMedia) {
					toast.info("Your media is still processing. Please wait for your media to be ready and try again.");
				} else {
					const emptyMessage = includeDocuments
						? "This session has no video, photo, or PDF to analyze. Add a file and try again."
						: "This session has no video or photo to analyze. Add a file and try again.";
					toast.info(emptyMessage);
				}
				selectedPack.value = null;
				return;
			}

			pendingAssets.value = assets;
			pendingMetadata.value = meta;
			loadedPackTasks.value = (packDetail as any).pack?.tasks || [];

			reportNameDraft.value = computeReportNameDraft(pack, meta, existingSparks);

			computePlatformRequirements(loadedPackTasks.value);
			await fetchUserInputConfigsForPack(pack.id, getId(), "contact", options.type, () => loadRequests.isSuperseded(requestId));

			// Re-check after the second await: a newer load may have started during the user-input fetch.
			if (loadRequests.isSuperseded(requestId)) {
				return;
			}

			// Auto-select all task IDs
			selectedTaskIds.value = [...new Set(loadedPackTasks.value.map((t) => t.id))];
		} catch (e: any) {
			// A superseded load's failure must not clobber the newer load's state or surface an error.
			if (loadRequests.isSuperseded(requestId)) {
				return;
			}
			// LSP-19467: ScribeFab toasts `error.value` verbatim, so the caught exception's own
			// `message` — SQL fragments, stack-shaped strings, internal hostnames — used to be read by
			// the customer. Only a written sentence goes in the ref; the raw error keeps its full
			// diagnostic value in the console and Datadog.
			logError("useRunAgentOrchestrator: failed to prepare pack", e, {
				packId: pack.id,
				entityType: options.type,
				entityId: getId()
			});
			error.value = couldNot("prepare this spark");
			selectedPack.value = null;
		} finally {
			// Only the latest load owns the loading flag; a superseded load leaves it to the newer one.
			if (!loadRequests.isSuperseded(requestId)) {
				loadingPack.value = false;
			}
		}
	};

	// Run the analysis.
	// LSP-20159: `onDispatchStarted` fires synchronously once a PROJECT run has passed its client-side
	// guards and its "Starting your report…" placeholder is in the store — i.e. before the slow
	// POST /projects/:id/sparks — so the caller can close the Sparky popup and show the reports list.
	const runAnalysis = async (runOptions: { onDispatchStarted?: () => void } = {}): Promise<boolean> => {
		// LSP-15921: re-entry guard — prevents a second click from kicking off a
		// duplicate run while the first invocation is still in flight (e.g. fast
		// double-tap before Vue propagates the disabled state to the DOM).
		// Both early-returns log so a future "Run button feels broken" incident
		// is a one-grep diagnosis instead of Datadog archaeology — see CLAUDE.md
		// "Diagnostic logging when fixing silent failures".
		if (crossTabPeerLockHeld()) {
			console.warn("[runAnalysis] cross-tab guard rejected — another tab is running this pack", {
				entityType: options.type,
				entityId: getId(),
				packId: selectedPack.value?.id
			});
			toast.info("This report is already running in another tab. Please wait for it to finish.");
			return false;
		}
		if (runningAnalysis.value) {
			console.warn("[runAnalysis] re-entry guard rejected duplicate invocation", {
				entityType: options.type,
				entityId: getId()
			});
			return false;
		}
		// LSP-21386: the dialog's Run button is the usual gate, but this is the single choke point every
		// run goes through (RunAgentDialog, ScribeFab's direct run), so refuse invalid inputs here too.
		if (!userInputComposable.allUserInputValid.value) {
			console.warn("[runAnalysis] user input invalid — aborting", {
				entityType: options.type,
				entityId: getId(),
				packId: selectedPack.value?.id,
				missing: userInputComposable.missingRequiredFieldLabels.value,
				invalid: userInputComposable.invalidFieldLabels.value
			});
			return false;
		}
		if (!selectedPack.value || !pendingAssets.value || !pendingMetadata.value) {
			console.warn("[runAnalysis] prerequisites missing — aborting", {
				entityType: options.type,
				entityId: getId(),
				hasSelectedPack: selectedPack.value != null,
				hasPendingAssets: pendingAssets.value != null,
				hasPendingMetadata: pendingMetadata.value != null
			});
			return false;
		}

		const pendingAssetList = pendingAssets.value;
		if (pendingAssetList.length === 0 || pendingAssetList.some((asset) => !asset?.externalId)) {
			console.warn("[runAnalysis] selected media missing — aborting", {
				entityType: options.type,
				entityId: getId(),
				packId: selectedPack.value.id,
				pendingAssetCount: pendingAssetList.length
			});
			error.value = SELECT_MEDIA_MESSAGE;
			toast.error(SELECT_MEDIA_MESSAGE);
			return false;
		}

		const entityName =
			options.type === "conversation" ? pendingMetadata.value?.conversation?.name : pendingMetadata.value?.project?.name;
		// Prefer the (possibly user-edited) draft — pre-filled by loadPackDetails with the
		// ordinal-enhanced default, and left as-is for packs whose dialog never showed the field.
		// Recomputed here only as a last-resort fallback (e.g. the draft's async compute failed).
		const draftTitle = reportNameDraft.value.trim();
		const analysisTitle = draftTitle
			? clampTitleGraphemes(draftTitle)
			: selectedPack.value.name && entityName
				? composeAutoTitle(selectedPack.value.name, entityName)
				: undefined;

		runningAnalysis.value = true;
		const crossTabAcquireKey = crossTabLockKey();
		if (crossTabAcquireKey) {
			try {
				localStorage.setItem(crossTabAcquireKey, JSON.stringify({ ts: Date.now(), tabId: crossTabTabId }));
			} catch (e) {
				console.warn("[runAnalysis] cross-tab lock write failed — proceeding without lock", {
					key: crossTabAcquireKey,
					error: String(e)
				});
			}
		}
		error.value = "";

		// LSP-20159: snapshot what this run targets BEFORE any await. saveTaskSelection and the create
		// request can take seconds; if the page switches entity (or the dialog resets) meanwhile, the
		// placeholder, the POST and its completion must all still refer to the run the user started.
		const entityId = getId();
		const runPack = selectedPack.value;
		const runUsesSelectedMedia = usingSelectedMedia.value;
		// Non-null while this run's placeholder still needs removing; the finally below removes it on
		// every path that does not reconcile it with a real analysis id.
		let startingReportClientId: string | null = null;

		try {
			if (options.type === "project") {
				startingReportClientId = store.addStartingReport({ externalType: "project", externalId: entityId, title: analysisTitle });
				try {
					runOptions.onDispatchStarted?.();
				} catch (callbackError) {
					console.warn("[runAnalysis] onDispatchStarted callback threw — continuing the run", {
						entityType: options.type,
						entityId,
						packId: runPack.id,
						error: String(callbackError)
					});
				}
			}

			// LSP-18409: the client quota bar counts the CALLER's Scribe tenant, which is wrong for a
			// cross-org project run — the OWNER org's cap applies and is enforced server-side in
			// createProjectSpark. Skip the client pre-check for projects so a caller whose OWN org is
			// capped isn't falsely blocked from running an uncapped owner's report. Conversations keep it.
			if (options.type !== "project") {
				await luckyQuota.refreshUsageCount();
				if (luckyQuota.hasReachedLimit.value) {
					const message = "You've used all of your free reports. Contact sales@liveswitch.com to upgrade.";
					error.value = message;
					toast.error(message);
					return false;
				}
			}

			const uniqueTaskIds = [...new Set(selectedTaskIds.value)];
			const finalSelection = { mode: "selected" as const, taskIds: uniqueTaskIds };

			const finalUserInputData: Record<string, Record<string, any>> = {};
			for (const [key, data] of Object.entries(userInputData.value)) {
				if (Object.keys(data).length > 0) {
					finalUserInputData[key] = data;
				}
			}

			try {
				await saveTaskSelection(runPack.id, selectedTaskIds.value);
			} catch (e) {
				console.error("Failed to save task selection", e);
			}

			// LSP-18409 (single-door): a PROJECT run goes through contact-api — it resolves the OWNER
			// org, enforces the OWNER's quota, dispatches under the owner's Scribe token, and bills the
			// owner. This replaces the browser gate + direct-Scribe dispatch (which homed and billed the
			// CALLER's org), so a cross-org run is owned and billed by the project owner and the report
			// lands in the owner tenant where both sides can see it. Conversations keep the browser path.
			if (options.type === "project") {
				let analysisId: string | undefined;
				let omittedDocuments: ExcludedDocument[] = [];
				try {
					const created = await api.createProjectSpark(entityId, {
						sparkId: runPack.id,
						sparkName: runPack.name,
						title: analysisTitle,
						userInputData: Object.keys(finalUserInputData).length > 0 ? finalUserInputData : undefined,
						taskIds: uniqueTaskIds.length > 0 ? uniqueTaskIds : undefined,
						// Carry the media selection so a selected-media run stays selected (not all-media),
						// and the documents flag — matching the browser gate the server now replaces.
						selectedFileIds: runUsesSelectedMedia ? pendingAssetList.map((asset) => asset.externalId) : undefined,
						includeDocuments
					});
					analysisId = created?.analysisId;
					omittedDocuments = created?.excludedDocuments ?? [];
				} catch (e: any) {
					// The server surfaces the specific reason (owner org at its report cap, view-only account,
					// or no analyzable media) as the error message — show those rather than the generic
					// fallback. LSP-20502: anything OUTSIDE that curated set is an unexpected throw and must
					// not reach the customer verbatim, here or via ScribeFab's toast of `error.value`.
					console.error("Failed to create project spark", e);
					const message = toUserFacingRunErrorFromThrown(e);
					error.value = message;
					// LSP-20159: informational, not red — a refused or failed start is not the user's fault,
					// and the message already says what to do next (the placeholder is removed in finally).
					toast.info(message);
					return false;
				}
				if (!analysisId) {
					console.warn("[runAnalysis] createProjectSpark returned no analysisId — treating the run as failed", {
						entityType: options.type,
						entityId,
						packId: runPack.id
					});
					toast.info(GENERIC_RUN_FAILURE_MESSAGE);
					return false;
				}
				// LSP-20159: hand the placeholder its real id; the reports list retires it once the row lands.
				if (startingReportClientId) {
					store.resolveStartingReport(startingReportClientId, analysisId);
					startingReportClientId = null;
				}
				// Optimistic UI + monitoring, mirroring the conversation path below (the server response
				// carries only the analysisId, so the initial status is a placeholder the monitor corrects).
				if (store.monitoringId === entityId && store.monitoringType === options.type) {
					const now = new Date();
					store.addOptimisticAnalysis({
						id: analysisId,
						externalType: "project",
						externalId: entityId,
						externalSource: "contact",
						status: AnalysisStatus.RUNNING,
						createdAt: now,
						updatedAt: now
					});
					void store.startMonitoring(
						entityId,
						options.type,
						() => {},
						// LSP-20502 (sibling path): the store hands this callback `e?.message` off the thrown
						// error, so an unexpected monitoring failure used to toast raw text at the customer.
						(e: string) => {
							toast.error(toUserFacingRunError(e));
						}
					);
				}
				try {
					if (Object.keys(finalUserInputData).length > 0) {
						await saveSessionUserInput("contact", options.type, entityId, runPack.id, finalUserInputData);
					}
				} catch (e) {
					console.error("Failed to save user input data", e);
				}
				toast.success("Sparky is running your Spark… Your report will be ready in a few minutes.");
				// Parity with the conversation path: tell the user immediately when an oversized document was
				// dropped from the run (the server also records it on the report row as a chip).
				if (omittedDocuments.length > 0) {
					toast.info(`${listExcludedDocuments(omittedDocuments)} was too large to include, so your report does not cover it.`);
				}
				// This run just changed the true prior-run count for this entity — a same-session reopen
				// must re-fetch, not reuse the now-stale cached list.
				existingSparksCache = null;
				return true;
			}

			// LSP-16433 (Option D): contact-api size-gates the documents (it does NOT dispatch — the server
			// cannot reliably mint a Scribe token in every environment), then the browser dispatches the
			// surviving media to Scribe directly. The gate covers both conversation and project runs.
			const selectedFileIds = runUsesSelectedMedia ? pendingAssetList.map((asset) => asset.externalId) : undefined;
			const gateBody = {
				packId: selectedPack.value.id,
				packName: selectedPack.value.name,
				selectedFileIds,
				includeDocuments
			};
			const gate =
				options.type === "conversation"
					? await api.gateConversationAnalysis(entityId, gateBody)
					: await api.gateProjectAnalysis(entityId, gateBody);

			const excludedIds = new Set<string>(gate?.excludedFileIds ?? []);
			const survivingAssets = Array.isArray(gate?.files)
				? gate.files
				: pendingAssetList.filter((asset) => !excludedIds.has(asset.externalId));
			// Documents ACTUALLY dropped from this run's payload. Unselected runs trust the server-assembled
			// entity payload, which can include media beyond the browser's preload. Selected runs still ignore
			// any gate omission outside the selected ids.
			const droppedDocuments = (gate?.excludedDocuments ?? []).filter((document) => {
				if (!excludedIds.has(document.fileId)) return false;
				return !selectedFileIds || selectedFileIds.includes(document.fileId);
			});

			if (survivingAssets.length === 0) {
				// Diagnostic breadcrumb (LSP-16433): this is an early return that aborts the run, so log WHY —
				// distinguishing "all media gated out as oversized" from "nothing was selected to run" — before
				// the user-facing toast. console.warn is forwarded to Datadog for contact-admin.
				console.warn("[runAnalysis] no media survived the document gate; aborting run", {
					entityId,
					type: options.type,
					pendingAssetCount: pendingAssetList.length,
					excludedCount: excludedIds.size
				});
				// Every selected item was an oversized document — there is nothing left to analyze. Name the
				// document(s) so the run is not a silent no-op (LSP-16433).
				if (droppedDocuments.length > 0) {
					toast.warning(
						`${listExcludedDocuments(droppedDocuments)} is too large to analyze, so there is nothing to run. Please remove or replace it and try again.`
					);
				} else {
					toast.error("We were unable to run your report. Please try again later.");
				}
				return false;
			}

			const response = await createAnalysis({
				externalType: options.type,
				externalId: entityId,
				externalSource: "contact",
				files: survivingAssets,
				packId: selectedPack.value.id,
				// analysisTitle now prefers the (possibly user-edited, ordinal-enhanced) draft over the
				// gate's plain server title — the gate has no idea about either. Falls back to the gate's
				// title only in the edge case where the client couldn't build one at all.
				title: analysisTitle ?? gate?.title,
				metadata: mergeAnalysisMetadata(pendingMetadata.value, gate?.metadata),
				selection: finalSelection,
				userInputData: Object.keys(finalUserInputData).length > 0 ? finalUserInputData : undefined
			});

			// Save user input data AFTER createAnalysis so the session is guaranteed to exist
			try {
				if (Object.keys(finalUserInputData).length > 0) {
					await saveSessionUserInput("contact", options.type, entityId, selectedPack.value.id, finalUserInputData);
				}
			} catch (e) {
				console.error("Failed to save user input data", e);
			}

			if (response != null && response.analysis != null) {
				// Only update the store if the user is still on the same entity; navigating away before
				// the response returns would otherwise corrupt the running state on the new page.
				if (store.monitoringId === entityId && store.monitoringType === options.type) {
					// Optimistic UI: add the new analysis immediately so the running indicator appears instantly.
					// Goes through addOptimisticAnalysis so the just-created id is protected from the composite
					// scope-aware drop until the status endpoint first reflects it (LSP-15648).
					store.addOptimisticAnalysis({
						id: response.analysis.id,
						externalType: response.analysis.externalType,
						externalId: response.analysis.externalId,
						externalSource: response.analysis.externalSource,
						status: response.analysis.status,
						createdAt: response.analysis.createdAt,
						updatedAt: response.analysis.updatedAt
					});
					void store.startMonitoring(
						entityId,
						options.type,
						() => {},
						// LSP-20502 (sibling path): the store hands this callback `e?.message` off the thrown
						// error, so an unexpected monitoring failure used to toast raw text at the customer.
						(e: string) => {
							toast.error(toUserFacingRunError(e));
						}
					);
				}

				// LSP-16433: now that the real Scribe analysisId exists, stamp it onto the gate's ledger row so the
				// omission can be merged onto this report row later (by analysisId — the only reliable bridge).
				// Best-effort: a failure here only costs the persistent chip/notice, not the run; log for triage.
				if (gate?.scheduleId && droppedDocuments.length > 0) {
					void api.linkAnalysisToSchedule(gate.scheduleId, response.analysis.id).catch((e) => {
						console.warn("[runAnalysis] failed to link analysisId to the excluded-documents ledger row", {
							scheduleId: gate.scheduleId,
							analysisId: response.analysis.id,
							error: String(e)
						});
					});
				}

				toast.success("Sparky is running your Spark… Your report will be ready in a few minutes.");
				// The report is running, but let the user know a document was actually dropped from it (LSP-16433).
				if (droppedDocuments.length > 0) {
					toast.info(`${listExcludedDocuments(droppedDocuments)} was too large to include, so your report does not cover it.`);
				}
				// See the project-path comment above: invalidate so a same-session reopen re-fetches.
				existingSparksCache = null;
				return true;
			} else if (response != null) {
				// LSP-20502: the response message was concatenated in unfiltered, so a raw server/network
				// string reached the customer. Route it through the same curated-reason filter, and set
				// error.value so this branch feeds ScribeFab the same sentence it toasts here.
				const message = toUserFacingRunError((response as any).message);
				error.value = message;
				toast.error(message);
			} else {
				error.value = GENERIC_RUN_FAILURE_MESSAGE;
				toast.error(GENERIC_RUN_FAILURE_MESSAGE);
			}
			return false;
		} catch (e: any) {
			console.error("Failed to run analysis", e);
			const message = toUserFacingRunErrorFromThrown(e);
			error.value = message;
			// Every other failure branch in this function toasts — a throw here (e.g. network
			// failure on the create call) must not end the flow with no user feedback.
			toast.error(message);
			return false;
		} finally {
			if (startingReportClientId) {
				store.removeStartingReport(startingReportClientId);
			}
			runningAnalysis.value = false;
			const crossTabReleaseKey = crossTabAcquireKey;
			if (crossTabReleaseKey) {
				const currentLock = crossTabReadLock(crossTabReleaseKey);
				if (currentLock?.tabId === crossTabTabId) {
					try {
						localStorage.removeItem(crossTabReleaseKey);
					} catch (e) {
						console.warn("[runAnalysis] cross-tab lock release failed", {
							key: crossTabReleaseKey,
							error: String(e)
						});
					}
				} else {
					console.warn("[runAnalysis] cross-tab lock release skipped — not owner", {
						key: crossTabReleaseKey,
						ourTabId: crossTabTabId,
						lockTabId: currentLock?.tabId ?? null,
						lockMissing: currentLock === null
					});
				}
			}
		}
	};

	const reset = () => {
		// LSP-16996: supersede any in-flight loadPackDetails() so a deferred load cannot resume after
		// reset() and repopulate the state we are about to clear.
		loadRequests.startRequest();
		selectedPack.value = null;
		loadedPackTasks.value = [];
		pendingAssets.value = null;
		pendingMetadata.value = null;
		runningAnalysis.value = false;
		loadingPack.value = false;
		error.value = null;
		selectedTaskIds.value = [];
		totalMediaCount.value = 0;
		readyMediaCount.value = 0;
		selectedMediaCount.value = 0;
		resetPlatforms();
		resetUserInput();
	};

	return {
		// State
		selectedPack,
		loadedPackTasks,
		pendingAssets,
		pendingMetadata,
		reportNameDraft,
		runningAnalysis,
		loadingPack,
		error,
		selectedTaskIds,

		// Media warnings
		mediaWarnings,
		hasNonReadyMedia,
		usingSelectedMedia,
		totalMediaCount,
		readyMediaCount,
		selectedMediaCount,

		// Computed
		needsDialog,

		// Sub-composables (exposed for dialog use)
		platformComposable,
		userInputComposable,

		// Methods
		loadPackDetails,
		runAnalysis,
		reset
	};
}

export type RunAgentOrchestrator = ReturnType<typeof useRunAgentOrchestrator>;
